import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "@/components/Header";
import TabGenerator from "@/components/TabGenerator";
import DrumTabViewer from "@/components/DrumTabViewer";
import VirtualDrumKit from "@/components/VirtualDrumKit";
import Metronome from "@/components/Metronome";
import LibrarySheet from "@/components/LibrarySheet";
import AuthModal from "@/components/AuthModal";
import PricingModal from "@/components/PricingModal";
import TeamSheet from "@/components/TeamSheet";
import ReferralModal from "@/components/ReferralModal";
import { PopularTabs } from "@/components/PopularTabs";
import { SuggestionModal } from "@/components/SuggestionModal";
import { LessonOfWeek } from "@/components/LessonOfWeek";
import { SongRequests } from "@/components/SongRequests";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, Timer, ArrowUpRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhY291c3RpYyUyMGRydW0lMjBraXQlMjBtdXNpYyUyMHN0dWRpbyUyMHNldHVwJTIwbmVvbiUyMGxpZ2h0aW5nfGVufDB8fHx8MTc5MjMwODc3OXww&ixlib=rb-4.1.0&q=85";

export default function Studio() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [metroOpen, setMetroOpen] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [libRefresh, setLibRefresh] = useState(0);
  const [libCount, setLibCount] = useState(0);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [lastGen, setLastGen] = useState(null);
  const initialQuery = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("q") || ""
    : "";

  const resultRef = useRef(null);
  const isPro = !!(user && user.is_pro);

  const refreshCount = useCallback(async () => {
    if (!user) { setLibCount(0); return; }
    try {
      const { data } = await api.get("/tabs");
      setLibCount(data.length);
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => { refreshCount(); }, [refreshCount, libRefresh]);

  const generate = async ({ query, difficulty, modifier }) => {
    if (!user) {
      setAuthOpen(true);
      toast.message("Log in to generate drum tabs");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/generate-tab", { query, difficulty, modifier });
      setTab(data.tab);
      setLastGen({ query, difficulty });
      if (data.usage) setUsage(data.usage);
      if (data.streak >= 2) toast.success(`🔥 ${data.streak}-day streak! Keep it going.`);
      refreshUser();
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      if (e.response?.status === 402) {
        setPricingOpen(true);
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Daily limit reached");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadBuiltTab = (t) => {
    setTab(t);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const onPatternSaved = (saved) => {
    setLibRefresh((n) => n + 1);
    setHighlightId(saved?.id || null);
    setLibOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] text-slate-100">
      <Header
        onOpenAuth={() => setAuthOpen(true)}
        onOpenMetronome={() => setMetroOpen(true)}
        onOpenLibrary={() => setLibOpen(true)}
        onOpenPricing={() => setPricingOpen(true)}
        onOpenTeam={() => setTeamOpen(true)}
        onOpenReferral={() => setReferralOpen(true)}
        libraryCount={libCount}
      />

      <TabGenerator onGenerate={generate} loading={loading} heroImage={HERO_IMG} initialQuery={initialQuery} />

      <main className="max-w-6xl mx-auto px-4 sm:px-8 pb-24">
        {user && usage && !usage.unlimited && (
          <div className="mb-5 -mt-2 flex items-center justify-center gap-2 text-sm">
            <span className="text-slate-400 font-tab">
              {usage.remaining} of {usage.limit} free tabs left today
            </span>
            <button
              onClick={() => setPricingOpen(true)}
              className="inline-flex items-center gap-1 text-[#ffab00] hover:underline font-semibold"
              data-testid="usage-upgrade-link"
            >
              <Crown className="w-3.5 h-3.5" /> Go unlimited
            </button>
          </div>
        )}
        <LessonOfWeek user={user} onOpen={loadBuiltTab} />
        <div ref={resultRef} className="scroll-mt-24">
          {tab ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <DrumTabViewer
                  tab={tab}
                  user={user}
                  isPro={isPro}
                  onUpgrade={() => setPricingOpen(true)}
                  onRequireAuth={() => setAuthOpen(true)}
                  onSaved={() => setLibRefresh((n) => n + 1)}
                  onRegenerate={lastGen ? (reason) => generate({ ...lastGen, modifier: reason }) : undefined}
                />
              </div>
              <div className="lg:col-span-1">
                <VirtualDrumKit onLoadTab={loadBuiltTab} isPro={!!user?.is_pro} onUpgrade={() => setPricingOpen(true)} onSaved={onPatternSaved} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 -mt-6">
              <div className="lg:col-span-2 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center flex flex-col items-center justify-center min-h-[320px]">
                <div className="flex items-end gap-1 h-10 mb-4">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className="wave-bar w-1.5 rounded-full bg-[#00f0ff]/60"
                      style={{ height: "100%", animationDelay: `${i * 0.1}s` }} />
                  ))}
                </div>
                <h3 className="font-heading text-2xl uppercase tracking-tight">Your tab appears here</h3>
                <p className="text-slate-500 text-sm mt-2 max-w-sm">
                  Search any song above and the AI will transcribe a playable drum groove you can see and hear instantly.
                </p>
              </div>
              <div className="lg:col-span-1">
                <VirtualDrumKit onLoadTab={loadBuiltTab} isPro={!!user?.is_pro} onUpgrade={() => setPricingOpen(true)} onSaved={onPatternSaved} />
              </div>
            </div>
          )}
        </div>
      </main>

      <PopularTabs />
      <SongRequests onRequireAuth={() => setAuthOpen(true)} />

      <footer className="border-t border-white/10 mt-8" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500 font-tab tracking-wide">
            © {new Date().getFullYear()} BeatFlow · AI Drum Tabs
          </p>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <SuggestionModal />
            <a
              href="https://drumkitpro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-[#ff6b00]/30 bg-[#ff6b00]/5 px-4 py-2 text-xs font-tab uppercase tracking-widest text-[#ff8c3a] transition-colors hover:bg-[#ff6b00]/15"
              data-testid="cross-app-pulse-link"
            >
              <Timer className="w-3.5 h-3.5" />
              Also try our Metronome &amp; Rudiment trainer — Pulse Rhythm
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <Metronome open={metroOpen} onOpenChange={setMetroOpen} />
      <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} onRequireAuth={() => setAuthOpen(true)} />
      <TeamSheet open={teamOpen} onOpenChange={setTeamOpen} />
      <ReferralModal open={referralOpen} onOpenChange={setReferralOpen} />
      <LibrarySheet
        open={libOpen}
        onOpenChange={(v) => { setLibOpen(v); if (!v) setHighlightId(null); }}
        onLoadTab={(t) => setTab(t)}
        refreshKey={libRefresh}
        highlightId={highlightId}
        onChanged={() => setLibRefresh((n) => n + 1)}
      />
    </div>
  );
}
